import React from "react";
import ArticlePageImage from "src/components/ArticlePageImage";
import { renderArticleRichText } from "src/services/PrismicService";

/**
 * Renders the body slices that news articles and press releases share: text, image, html, embed,
 * column tables and anchor. Returns null for any other slice type.
 */
export const renderBasicSlice = (slice: any, index: number, body: any[]) => {
  // Anchor
  if (slice?.type === "anchor") {
    const id = slice?.primary?.anchor_id;
    if (!id) {
      return null;
    }
    return <div key={index} id={id} />;
  }

  // Text Slice
  if (slice.type === "text") {
    return (
      <div key={index} className="rotoql-content-article__rich-text">
        {renderArticleRichText(slice.primary.text)}
      </div>
    );
  }

  if (slice.type === "image") {
    const { image, hyperlink, formatting } = slice.primary;

    let imageDom = (
      <ArticlePageImage
        className={`rotoql-content-article__image ${
          // Featured image should only have index 0 or 1 default should be 0 but if we have anchor (table-of-content) it might change to 1
          index ? "rotoql-content-article__not-first-image" : ""
        }`}
        image={image}
      />
    );

    // Image with link
    if (hyperlink && hyperlink._linkType === "Link.web" && hyperlink.url) {
      imageDom = (
        <a href={hyperlink.url} target={hyperlink.target} aria-label={`Link to ${hyperlink.url}`}>
          {imageDom}
        </a>
      );
    }

    // Inline image (left side)
    const shouldDisplayInline = body[index + 1] && body[index + 1].type === "text" && formatting === "Inline";

    return (
      <div
        key={index}
        className={`rotoql-content-article__image-container ${
          shouldDisplayInline ? "rotoql-content-article__image-container--inline" : ""
        }`}
      >
        {imageDom}
        <div className="rotoql-content-article__image-meta">
          <div className="rotoql-content-article__image-copyright">{image?.copyright}</div>
        </div>
      </div>
    );
  }

  // HTML slice
  if (slice.type === "html") {
    return (
      <div
        key={index}
        className="rotoql-content-article__html-container"
        dangerouslySetInnerHTML={{
          __html: slice.primary.html,
        }}
      />
    );
  }

  // Embed slice
  if (slice.type === "embed") {
    const isYouTube = slice.primary.embed_url.html.includes("youtube.com");
    const isAudacy = slice.primary.embed_url.html.includes("audacy.com");
    return (
      <div
        key={index}
        className={`rotoql-content-article__embed-container ${isYouTube ? "youtube" : ""} ${isAudacy ? "audacy" : ""}`}
        dangerouslySetInnerHTML={{
          __html: slice.primary.embed_url.html,
        }}
      />
    );
  }

  // Table
  if (slice.type?.includes("column_table")) {
    return (
      <div key={index} className="rotoql-content-article__table-container">
        <div className="rotoql-content-article__table">
          <table>
            <tbody>
              {slice.fields.map((row: any, rowindex: number) => (
                <tr key={rowindex}>
                  {Object.keys(row)
                    .filter((r) => r.includes("column"))
                    .map((colname, colindex) => (
                      <td key={colindex}>{renderArticleRichText(row[colname])}</td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
};
